import { Component, ChangeDetectionStrategy, Injectable, signal } from '@angular/core';


/**
 * Toast notification system.
 *
 * Two parts:
 * 1. ToastService — injectable service to show toasts from anywhere
 * 2. <mypt-toast-container> — placed once in the app shell, renders toasts
 *
 * Position: top-right on desktop, top-center on mobile.
 * Swipe-to-dismiss on mobile via pointer events.
 */
export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: number;
  message: string;
  type: ToastType;
  duration: number;
  visible: boolean;
  dismissing: boolean;
}

@Injectable({ providedIn: 'root' })
export class ToastService {
  private nextId = 0;
  readonly toasts = signal<Toast[]>([]);

  show(message: string, type: ToastType = 'info', duration = 4000): void {
    const toast: Toast = {
      id: this.nextId++,
      message,
      type,
      duration,
      visible: false,
      dismissing: false,
    };
    this.toasts.update(list => [...list, toast]);

    // Trigger enter animation
    requestAnimationFrame(() => {
      this.toasts.update(list =>
        list.map(t => t.id === toast.id ? { ...t, visible: true } : t)
      );
    });

    // Auto-dismiss
    if (duration > 0) {
      setTimeout(() => this.dismiss(toast.id), duration);
    }
  }

  success(message: string, duration?: number): void { this.show(message, 'success', duration); }
  error(message: string, duration?: number): void { this.show(message, 'error', duration ?? 6000); }
  warning(message: string, duration?: number): void { this.show(message, 'warning', duration); }
  info(message: string, duration?: number): void { this.show(message, 'info', duration); }

  dismiss(id: number): void {
    this.toasts.update(list =>
      list.map(t => t.id === id ? { ...t, dismissing: true } : t)
    );
    // Remove after exit animation
    setTimeout(() => {
      this.toasts.update(list => list.filter(t => t.id !== id));
    }, 250);
  }
}

@Component({
    selector: 'mypt-toast-container',
    imports: [],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
    <div class="toast-container" role="region" aria-label="Uyarılar" aria-live="polite">
      @for (toast of toastService.toasts(); track toast.id) {
        <div
          class="toast"
          [class]="'toast toast--' + toast.type"
          [class.toast--visible]="toast.visible"
          [class.toast--dismissing]="toast.dismissing"
          [style.transform]="getSwipeTransform(toast.id)"
          (pointerdown)="onSwipeStart($event, toast.id)"
          (pointermove)="onSwipeMove($event, toast.id)"
          (pointerup)="onSwipeEnd(toast.id)"
          (pointercancel)="onSwipeEnd(toast.id)"
          [attr.role]="toast.type === 'error' || toast.type === 'warning' ? 'alert' : 'status'"
        >
          <span class="toast__icon" aria-hidden="true">
            @switch (toast.type) {
              @case ('success') {
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M5 10l3 3 7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              }
              @case ('error') {
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M6 6l8 8M14 6l-8 8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              }
              @case ('warning') {
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 6v5M10 14v0" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              }
              @case ('info') {
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 9v5M10 6v0" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              }
            }
          </span>
          <span class="toast__message">{{ toast.message }}</span>
          <button
            class="toast__close"
            (click)="toastService.dismiss(toast.id)"
            aria-label="Kapat"
            type="button"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 4l8 8M12 4L4 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      }
    </div>
  `,
    styles: [`
    .toast-container {
      position: fixed;
      z-index: 1300;
      top: 1.25rem;
      right: 1.25rem;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      max-width: 420px;
      pointer-events: none;

      @media (max-width: 767.98px) {
        top: calc(0.75rem + env(safe-area-inset-top, 0px));
        right: 0.75rem;
        left: 0.75rem;
        max-width: none;
      }
    }

    .toast {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.5rem 0.5rem 0.5rem 1rem;
      background: var(--mypt-surface);
      border: 1px solid var(--mypt-border);
      border-left: 4px solid var(--mypt-info);
      border-radius: var(--radius-md);
      box-shadow: var(--shadow-3);
      pointer-events: auto;
      touch-action: pan-x;
      opacity: 0;
      transform: translateY(-8px);

      &--visible { opacity: 1; transform: none; }
      &--dismissing { opacity: 0; transform: translateY(-8px); }

      &--success { border-left-color: var(--mypt-success); .toast__icon { color: var(--mypt-success); } }
      &--error   { border-left-color: var(--mypt-danger);  .toast__icon { color: var(--mypt-danger); } }
      &--warning { border-left-color: var(--mypt-warning); .toast__icon { color: var(--mypt-warning); } }
      &--info    { border-left-color: var(--mypt-info);    .toast__icon { color: var(--mypt-info); } }
    }

    @media (prefers-reduced-motion: no-preference) {
      .toast { transition: transform 0.24s var(--ease-out), opacity 0.24s var(--ease-out); }
    }

    .toast__icon { display: flex; flex-shrink: 0; }

    .toast__message {
      flex: 1;
      font-size: 0.9375rem;
      font-weight: 600;
      line-height: 1.4;
      color: var(--mypt-text);
    }

    .toast__close {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 44px;
      height: 44px;
      border: 0;
      border-radius: 999px;
      background: none;
      color: var(--mypt-text-muted);
      cursor: pointer;

      &:hover { background: var(--mypt-surface-2); }
      &:focus-visible { outline: 3px solid var(--mypt-focus); outline-offset: 2px; }
    }
  `]
})
export class ToastContainerComponent {
  constructor(public toastService: ToastService) {}

  // ── Swipe-to-dismiss tracking ──
  private swipeState = new Map<number, { startX: number; currentX: number; active: boolean }>();

  onSwipeStart(event: PointerEvent, id: number): void {
    this.swipeState.set(id, { startX: event.clientX, currentX: 0, active: true });
    (event.target as HTMLElement).setPointerCapture(event.pointerId);
  }

  onSwipeMove(event: PointerEvent, id: number): void {
    const state = this.swipeState.get(id);
    if (!state?.active) return;
    state.currentX = event.clientX - state.startX;
  }

  onSwipeEnd(id: number): void {
    const state = this.swipeState.get(id);
    if (!state) return;
    // Dismiss if swiped > 100px
    if (Math.abs(state.currentX) > 100) {
      this.toastService.dismiss(id);
    }
    this.swipeState.delete(id);
  }

  getSwipeTransform(id: number): string | null {
    const state = this.swipeState.get(id);
    if (!state?.active || !state.currentX) return null;
    return `translateX(${state.currentX}px)`;
  }
}
